import React from "react";
import MedicalConditionCard from "./MedicalConditionCard";
import { isStatusTrue } from "../utils/patientUtils";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

interface MedicalTabProps {
  patientData: any;
  theme?: "light" | "dark";
}

const MedicalTab: React.FC<MedicalTabProps> = ({ patientData, theme = "light" }) => {
  const medicalConditions = [
    { key: "gastricProblem", label: "Gastric Problem" },
    { key: "highBP", label: "High BP" },
    { key: "diabetes", label: "Diabetes" },
    { key: "monthlyInjection", label: "Monthly Injection" },
    { key: "pregnancyBreastFeeding", label: "Pregnancy/Breast Feeding" },
    { key: "coughColdFever", label: "Cough/Cold/Fever" },
    { key: "chestpainHeartProblem", label: "Chest Pain/Heart" },
    { key: "epilepsy", label: "Epilepsy" },
    { key: "previousExtraction", label: "Previous Extraction" },
    { key: "previoussurgeries", label: "Previous Surgeries" },
    { key: "covid193Doses", label: "COVID-19 (3 Doses)" },
    { key: "drugHistory", label: "Drug History" },
    { key: "dietaryHistory", label: "Dietary History" },
    { key: "brushingHistory", label: "Brushing History" },
    { key: "medicalHistoryNote", label: "Medical History (Note)" },
    { key: "oralHygieneHistory", label: "Oral Hygiene History" },
    { key: "habitHistory", label: "Habit History" },
    { key: "familyHistory", label: "Family History" },
    { key: "socialHistory", label: "Social History" },
  ];

  const allergies = patientData?.allergies;
  const hasAllergiesItems = Array.isArray(allergies?.items) && allergies.items.length > 0;
  const allergiesStatusTrue = isStatusTrue(allergies?.status);
  const allergiesNote = allergies?.note && String(allergies.note).trim();

  const diabetesDrugs = patientData?.diabetesMellitusDrugs;
  const hasDiabetesDrugsItems = Array.isArray(diabetesDrugs?.items) && diabetesDrugs.items.length > 0;
  const diabetesDrugsStatusTrue = isStatusTrue(diabetesDrugs?.status);
  const diabetesDrugsNote = diabetesDrugs?.note && String(diabetesDrugs.note).trim();

  const hypertensionDrugs = patientData?.hypertensionDrugs;
  const hasHypertensionDrugsItems = Array.isArray(hypertensionDrugs?.items) && hypertensionDrugs.items.length > 0;
  const hypertensionDrugsStatusTrue = isStatusTrue(hypertensionDrugs?.status);
  const hypertensionDrugsNote = hypertensionDrugs?.note && String(hypertensionDrugs.note).trim();

  const dyslipidemiaDrugs = patientData?.dyslipidemiaDrugs;
  const hasDyslipidemiaDrugsItems = Array.isArray(dyslipidemiaDrugs?.items) && dyslipidemiaDrugs.items.length > 0;
  const dyslipidemiaDrugsStatusTrue = isStatusTrue(dyslipidemiaDrugs?.status);
  const dyslipidemiaDrugsNote = dyslipidemiaDrugs?.note && String(dyslipidemiaDrugs.note).trim();

  const InfoCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className={`rounded-lg p-3 ${
      theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
    }`}>
      <p className={`text-xs uppercase tracking-wide mb-1 ${
        theme === "dark" ? "text-slate-400" : "text-slate-500"
      }`}>{label}</p>
      <p className={`text-sm font-medium ${
        theme === "dark" ? "text-slate-200" : "text-slate-800"
      }`}>{value || "N/A"}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h4 className={`text-sm font-semibold mb-3 ${
          theme === "dark" ? "text-slate-300" : "text-slate-700"
        }`}>Medical Conditions</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
          {/* Allergies: support old (status + note) and new (status + items) */}
          <div className={`rounded-lg p-3 ${
            allergiesStatusTrue
              ? theme === "dark"
                ? "bg-red-900/30 border border-red-800"
                : "bg-red-50 border border-red-100"
              : theme === "dark"
              ? "bg-slate-700/50"
              : "bg-slate-50"
          }`}>
            <div className="flex items-center justify-between">
              <p className={`text-sm font-medium ${
                theme === "dark" ? "text-slate-300" : "text-slate-700"
              }`}>Allergies</p>
              {allergiesStatusTrue ? (
                <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
              ) : (
                <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
              )}
            </div>
            {allergiesStatusTrue && (
              <div className="mt-2 space-y-1">
                {hasAllergiesItems ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className={theme === "dark" ? "text-slate-400" : "text-slate-500"}>
                          <th className="text-left py-1 pr-2 font-medium">#</th>
                          <th className="text-left py-1 pr-2 font-medium">Type</th>
                          <th className="text-left py-1 pr-2 font-medium">Note</th>
                          <th className="text-left py-1 pr-2 font-medium">Dosage</th>
                          <th className="text-left py-1 font-medium">When diagnosed</th>
                        </tr>
                      </thead>
                      <tbody className={theme === "dark" ? "text-red-300" : "text-red-600"}>
                        {allergies.items.map((item: any, index: number) => (
                          <tr key={item?.id ?? index}>
                            <td className="py-0.5 pr-2">{index + 1}</td>
                            <td className="py-0.5 pr-2">{item?.label ?? item?.id ?? "—"}</td>
                            <td className="py-0.5 pr-2">{item?.note ? String(item.note) : "—"}</td>
                            <td className="py-0.5 pr-2">{item?.dosage ? String(item.dosage) : "—"}</td>
                            <td className="py-0.5">{item?.diagnosedWhen ? String(item.diagnosedWhen) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {allergiesNote && !hasAllergiesItems && (
                  <p className={`text-xs ${theme === "dark" ? "text-red-300" : "text-red-600"}`}>Note: {allergiesNote}</p>
                )}
                {allergiesNote && hasAllergiesItems && (
                  <p className={`text-xs mt-1 ${theme === "dark" ? "text-red-300" : "text-red-600"}`}>Note: {allergiesNote}</p>
                )}
              </div>
            )}
          </div>
          {/* Diabetes mellitus – Drugs: support old (status + note) and new (status + items) */}
          <div className={`rounded-lg p-3 ${
            diabetesDrugsStatusTrue
              ? theme === "dark"
                ? "bg-red-900/30 border border-red-800"
                : "bg-red-50 border border-red-100"
              : theme === "dark"
              ? "bg-slate-700/50"
              : "bg-slate-50"
          }`}>
            <div className="flex items-center justify-between">
              <p className={`text-sm font-medium ${
                theme === "dark" ? "text-slate-300" : "text-slate-700"
              }`}>Diabetes mellitus – Drugs</p>
              {diabetesDrugsStatusTrue ? (
                <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
              ) : (
                <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
              )}
            </div>
            {diabetesDrugsStatusTrue && (
              <div className="mt-2 space-y-1">
                {hasDiabetesDrugsItems ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className={theme === "dark" ? "text-slate-400" : "text-slate-500"}>
                          <th className="text-left py-1 pr-2 font-medium">#</th>
                          <th className="text-left py-1 pr-2 font-medium">Drug</th>
                          <th className="text-left py-1 pr-2 font-medium">Note</th>
                          <th className="text-left py-1 pr-2 font-medium">Dosage</th>
                          <th className="text-left py-1 font-medium">When diagnosed</th>
                        </tr>
                      </thead>
                      <tbody className={theme === "dark" ? "text-red-300" : "text-red-600"}>
                        {diabetesDrugs.items.map((item: any, index: number) => (
                          <tr key={item?.id ?? index}>
                            <td className="py-0.5 pr-2">{index + 1}</td>
                            <td className="py-0.5 pr-2">{item?.label ?? item?.id ?? "—"}</td>
                            <td className="py-0.5 pr-2">{item?.note ? String(item.note) : "—"}</td>
                            <td className="py-0.5 pr-2">{item?.dosage ? String(item.dosage) : "—"}</td>
                            <td className="py-0.5">{item?.diagnosedWhen ? String(item.diagnosedWhen) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {diabetesDrugsNote && !hasDiabetesDrugsItems && (
                  <p className={`text-xs ${theme === "dark" ? "text-red-300" : "text-red-600"}`}>Note: {diabetesDrugsNote}</p>
                )}
                {diabetesDrugsNote && hasDiabetesDrugsItems && (
                  <p className={`text-xs mt-1 ${theme === "dark" ? "text-red-300" : "text-red-600"}`}>Note: {diabetesDrugsNote}</p>
                )}
              </div>
            )}
          </div>
          {/* Hypertension – Drugs: note only */}
          <div className={`rounded-lg p-3 ${
            hypertensionDrugsStatusTrue
              ? theme === "dark"
                ? "bg-red-900/30 border border-red-800"
                : "bg-red-50 border border-red-100"
              : theme === "dark"
              ? "bg-slate-700/50"
              : "bg-slate-50"
          }`}>
            <div className="flex items-center justify-between">
              <p className={`text-sm font-medium ${
                theme === "dark" ? "text-slate-300" : "text-slate-700"
              }`}>Hypertension – Drugs</p>
              {hypertensionDrugsStatusTrue ? (
                <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
              ) : (
                <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
              )}
            </div>
            {hypertensionDrugsStatusTrue && (
              <div className="mt-2 space-y-1">
                {hasHypertensionDrugsItems ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className={theme === "dark" ? "text-slate-400" : "text-slate-500"}>
                          <th className="text-left py-1 pr-2 font-medium">#</th>
                          <th className="text-left py-1 pr-2 font-medium">Drug</th>
                          <th className="text-left py-1 font-medium">Note</th>
                        </tr>
                      </thead>
                      <tbody className={theme === "dark" ? "text-red-300" : "text-red-600"}>
                        {hypertensionDrugs.items.map((item: any, index: number) => (
                          <tr key={item?.id ?? index}>
                            <td className="py-0.5 pr-2">{index + 1}</td>
                            <td className="py-0.5 pr-2">{item?.label ?? item?.id ?? "—"}</td>
                            <td className="py-0.5">{item?.note ? String(item.note) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {hypertensionDrugsNote && !hasHypertensionDrugsItems && (
                  <p className={`text-xs ${theme === "dark" ? "text-red-300" : "text-red-600"}`}>Note: {hypertensionDrugsNote}</p>
                )}
                {hypertensionDrugsNote && hasHypertensionDrugsItems && (
                  <p className={`text-xs mt-1 ${theme === "dark" ? "text-red-300" : "text-red-600"}`}>Note: {hypertensionDrugsNote}</p>
                )}
              </div>
            )}
          </div>
          {/* Dyslipidemia: note only */}
          <div className={`rounded-lg p-3 ${
            dyslipidemiaDrugsStatusTrue
              ? theme === "dark"
                ? "bg-red-900/30 border border-red-800"
                : "bg-red-50 border border-red-100"
              : theme === "dark"
              ? "bg-slate-700/50"
              : "bg-slate-50"
          }`}>
            <div className="flex items-center justify-between">
              <p className={`text-sm font-medium ${
                theme === "dark" ? "text-slate-300" : "text-slate-700"
              }`}>Dyslipidemia</p>
              {dyslipidemiaDrugsStatusTrue ? (
                <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
              ) : (
                <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
              )}
            </div>
            {dyslipidemiaDrugsStatusTrue && (
              <div className="mt-2 space-y-1">
                {hasDyslipidemiaDrugsItems ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className={theme === "dark" ? "text-slate-400" : "text-slate-500"}>
                          <th className="text-left py-1 pr-2 font-medium">#</th>
                          <th className="text-left py-1 pr-2 font-medium">Drug</th>
                          <th className="text-left py-1 font-medium">Note</th>
                        </tr>
                      </thead>
                      <tbody className={theme === "dark" ? "text-red-300" : "text-red-600"}>
                        {dyslipidemiaDrugs.items.map((item: any, index: number) => (
                          <tr key={item?.id ?? index}>
                            <td className="py-0.5 pr-2">{index + 1}</td>
                            <td className="py-0.5 pr-2">{item?.label ?? item?.id ?? "—"}</td>
                            <td className="py-0.5">{item?.note ? String(item.note) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {dyslipidemiaDrugsNote && !hasDyslipidemiaDrugsItems && (
                  <p className={`text-xs ${theme === "dark" ? "text-red-300" : "text-red-600"}`}>Note: {dyslipidemiaDrugsNote}</p>
                )}
                {dyslipidemiaDrugsNote && hasDyslipidemiaDrugsItems && (
                  <p className={`text-xs mt-1 ${theme === "dark" ? "text-red-300" : "text-red-600"}`}>Note: {dyslipidemiaDrugsNote}</p>
                )}
              </div>
            )}
          </div>
          {medicalConditions.map((condition) => (
            <MedicalConditionCard
              key={condition.key}
              label={condition.label}
              condition={patientData?.[condition.key] || {}}
              theme={theme}
            />
          ))}
        </div>
      </div>

      <div>
        <h4 className={`text-sm font-semibold mb-3 ${
          theme === "dark" ? "text-slate-300" : "text-slate-700"
        }`}>Examination Notes</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          <InfoCard 
            label="Extra Oral Examination" 
            value={patientData?.extraOralExamination} 
          />
          <InfoCard 
            label="Intra Oral Examination" 
            value={patientData?.intraOralExamination} 
          />
        </div>
      </div>
    </div>
  );
};

export default MedicalTab;

